import { createPartnerApiToken } from "@redotech/admin-sdk/rpc/schema/partners/create-partner-api-token";
import { useRequiredContext } from "@redotech/react-util/context";
import CheckIcon from "@redotech/redo-icons/check.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import KeyIcon from "@redotech/redo-icons/key-01.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { copyToClipboard } from "@redotech/redo-web/clipboard";
import { Flex } from "@redotech/redo-web/flex";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { memo, useState } from "react";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";

/**
 * Names a new partner API token, then shows its secret once. The secret lives
 * only in this modal's state and is gone when it closes.
 */
export const CreatePartnerApiTokenModal = memo(
  function CreatePartnerApiTokenModal({
    partnerId,
    partnerName,
    onClose,
  }: {
    partnerId: string;
    partnerName: string;
    onClose: () => void;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [name, setName] = useState("");
    const [secret, setSecret] = useState<string | undefined>();
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const input = createPartnerApiToken.input.safeParse({ partnerId, name });

    if (secret !== undefined) {
      return (
        <RedoModal
          disableBackdropClose
          onModalCloseRequested={onClose}
          primaryButton={{ text: "Done", onClick: onClose }}
          subtitle={`Send this to ${partnerName} over a secure channel. You won't be able to see it again.`}
          title="New partner API token"
          TitleIcon={KeyIcon}
        >
          <Flex dir="column" gap="lg">
            <Flex align="flex-end" gap="md">
              <Flex grow={1}>
                <RedoTextInput
                  ariaLabel="Token"
                  autoCompleteDisabled
                  fullWidth
                  setValue={() => {}}
                  state="readonly"
                  value={secret}
                />
              </Flex>
              <RedoButton
                hierarchy="secondary"
                IconLeading={copied ? CheckIcon : CopyIcon}
                onClick={async () => {
                  await copyToClipboard(secret);
                  setCopied(true);
                }}
                size="sm"
                text={copied ? "Copied" : "Copy"}
              />
            </Flex>
            <RedoAlert
              color="warning"
              description="If it's lost, revoke it and create a new one."
              size="sm"
              title="Shown only once"
            />
          </Flex>
        </RedoModal>
      );
    }

    // Closing mid-request would drop the one-time secret of a token that still gets created.
    const requestClose = () => {
      if (!submitting) {
        onClose();
      }
    };

    const submit = async () => {
      if (!input.success || submitting) {
        return;
      }
      setSubmitting(true);
      setError(undefined);
      try {
        setSecret((await rpcClient.createPartnerApiToken(input.data)).secret);
      } catch (err) {
        setError(clientErrorMessage(err, "Couldn't create the token."));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <RedoModal
        disableBackdropClose={submitting}
        onModalCloseRequested={requestClose}
        primaryButton={{
          text: "Create token",
          disabled: !input.success || submitting,
          onClick: submit,
        }}
        secondaryButton={{
          text: "Cancel",
          disabled: submitting,
          onClick: requestClose,
        }}
        subtitle={`${partnerName} uses this token to create and manage stores through the partner API.`}
        title="Create partner API token"
        TitleIcon={KeyIcon}
      >
        <RedoTextInput
          autoFocus
          errorText={error}
          fullWidth
          label="Name"
          placeholder="Production"
          setValue={setName}
          value={name}
        />
      </RedoModal>
    );
  },
);
